import React from 'react';
import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { FieldMetadata } from '../../config/userUploaderConfig';
import CsvColumnSelector from './CsvColumnSelector';
import CustomValueInput from './CustomValueInput';

export interface FieldMappingRowValue {
  enabled: boolean;
  csvColumn: string | null;
  csvColumns: string[] | null;
  customValue: unknown;
  matchConfidence?: number;
  isCoordinateSplit?: boolean;
}

interface FieldMappingRowProps {
  field: FieldMetadata;
  csvHeaders: string[];
  csvSampleData?: Record<string, string>[];
  value: FieldMappingRowValue;
  onChange: (value: FieldMappingRowValue) => void;
  disabled?: boolean;
}

const FieldMappingRow: React.FC<FieldMappingRowProps> = ({
  field,
  csvHeaders,
  csvSampleData,
  value,
  onChange,
  disabled = false,
}) => {
  const isMultiColumn = field.parser === 'multi_column' || field.parser === 'crops';
  const isCoordinateSplit = field.parser === 'coordinate_split';

  const handleToggle = (checked: boolean) => {
    onChange({ ...value, enabled: checked });
  };

  const handleCsvChange = (csvValue: string | string[] | null) => {
    if (Array.isArray(csvValue)) {
      onChange({ ...value, csvColumns: csvValue, csvColumn: null });
    } else {
      onChange({ ...value, csvColumn: csvValue, csvColumns: null });
    }
  };

  const handleCustomChange = (customValue: unknown) => {
    onChange({ ...value, customValue });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(180px, 1fr) minmax(200px, 1.5fr) minmax(140px, 1fr)',
        gap: 1.5,
        alignItems: 'center',
        py: 0.75,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Checkbox
          checked={value.enabled}
          disabled={disabled || field.requiredApi}
          size="small"
          onChange={(e) => handleToggle(e.target.checked)}
          sx={{ p: 0.5 }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: field.requiredApi ? 600 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {field.displayName}
          {field.requiredApi && <span style={{ color: 'error.main' }}> *</span>}
        </Typography>
        {field.helperText && (
          <Tooltip title={field.helperText}>
            <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5, flexShrink: 0, opacity: 0.5 }} />
          </Tooltip>
        )}
      </Box>

      <CsvColumnSelector
        csvHeaders={csvHeaders}
        csvSampleData={csvSampleData}
        value={isMultiColumn ? (value.csvColumns || []) : (value.csvColumn || null)}
        onChange={handleCsvChange}
        multiSelect={isMultiColumn}
        matchConfidence={value.matchConfidence}
        isCoordinateSplit={isCoordinateSplit && !!value.csvColumn}
        disabled={disabled || !value.enabled}
      />

      {field.supportsDefault ? (
        <CustomValueInput
          field={field}
          value={value.customValue !== undefined ? value.customValue : field.defaultValue}
          onChange={handleCustomChange}
          disabled={disabled || !value.enabled}
        />
      ) : (
        <Typography variant="caption" color="textSecondary">
          {isMultiColumn ? 'Multi-columna' : isCoordinateSplit ? 'Divisor' : ''}
        </Typography>
      )}
    </Box>
  );
};

export default FieldMappingRow;
